import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button.js';
import { cn } from './cn.js';

function useOverlay(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open, onClose]);
  return panel;
}

const backdrop = 'fixed inset-0 z-40 bg-gray-900/40 backdrop-blur-[2px] animate-[fade_200ms_ease-out]';

/** Fenêtre modale centrée. */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 520,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const panel = useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className={backdrop} onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
        <div
          ref={panel}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : undefined}
          style={{ maxWidth: width }}
          className="w-full rounded-md border border-line-soft bg-surface shadow-od-3 outline-none focus-visible:outline-none"
        >
          <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
            <h2 className="font-display text-base font-bold text-ink">{title}</h2>
            <IconButton label="Fermer" size="sm" onClick={onClose}>
              <X size={18} strokeWidth={1.75} />
            </IconButton>
          </div>
          <div className="px-5 py-4">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 border-t border-line-soft px-5 py-3">{footer}</div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Panneau latéral. Plein écran sur mobile. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 640,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const panel = useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className={backdrop} onClick={onClose} aria-hidden />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ maxWidth: width }}
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-line-soft bg-surface shadow-od-3 outline-none focus-visible:outline-none',
          'animate-[slidein_320ms_cubic-bezier(0.22,0.61,0.36,1)]',
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line-soft px-6 py-4">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold leading-snug text-ink">{title}</h2>
            {subtitle && <div className="mt-1 text-[13px] text-ink-2">{subtitle}</div>}
          </div>
          <IconButton label="Fermer" size="sm" onClick={onClose}>
            <X size={18} strokeWidth={1.75} />
          </IconButton>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line-soft px-6 py-3">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}
